import React, { useRef, useEffect, useState, useCallback } from "react";
import Hls from "hls.js";
import { Play, Pause, Volume2, VolumeX, Maximize, Settings, RotateCcw, RotateCw } from "lucide-react";
import { updateLessonWatchTime } from '../../api/packageService';

// ── Segment cache ─────────────────────────────────────────────────────────────
// Stores downloaded HLS segments so backward seeks are instant (no re-download).
// Uses LRU eviction so memory stays bounded.
class LRUSegmentCache {
  private cache = new Map<string, ArrayBuffer>();
  private readonly maxEntries: number;

  constructor(maxEntries = 300) { this.maxEntries = maxEntries; }

  get(url: string): ArrayBuffer | undefined {
    const item = this.cache.get(url);
    if (item) {
      // Move to end = mark as most-recently used
      this.cache.delete(url);
      this.cache.set(url, item);
    }
    return item;
  }

  set(url: string, data: ArrayBuffer): void {
    if (this.cache.has(url)) this.cache.delete(url);
    else if (this.cache.size >= this.maxEntries) {
      // Evict the oldest entry (first in Map iteration order)
      this.cache.delete(this.cache.keys().next().value as string);
    }
    this.cache.set(url, data);
  }

  clear(): void { this.cache.clear(); }
  get size(): number { return this.cache.size; }
}

// ── Custom HLS.js fragment loader ────────────────────────────────────────────
// Wraps the default XHR loader: serves cache hits immediately, stores misses.
function makeCachedFragLoader(cache: LRUSegmentCache) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const DefaultLoader = (Hls as any).DefaultConfig.loader;

  return class CachedFragLoader extends DefaultLoader {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(config: any) { super(config); }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    load(context: any, loaderConfig: any, callbacks: any): void {
      const url: string = context.url;
      const cached = cache.get(url);

      if (cached) {
        // Cache hit — return a copy immediately (next microtask to stay async)
        const buf = cached.slice(0);
        const now = performance.now();
        const stats = {
          aborted: false, loaded: buf.byteLength, total: buf.byteLength,
          retry: 0, chunkCount: 1, bwEstimate: 0,
          loading:  { start: now, first: now, end: now },
          parsing:  { start: now, end: now },
          buffering: { start: now, first: now, end: now },
        };
        Promise.resolve().then(() =>
          callbacks.onSuccess({ url, data: buf }, stats, context, null)
        );
        return;
      }

      // Cache miss — download normally, then store the result
      const origSuccess = callbacks.onSuccess;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      callbacks.onSuccess = (response: any, stats: any, ctx: any, net: any) => {
        if (response.data instanceof ArrayBuffer && response.data.byteLength > 0) {
          cache.set(url, response.data.slice(0)); // slice = own copy
        }
        origSuccess(response, stats, ctx, net);
      };

      super.load(context, loaderConfig, callbacks);
    }
  };
}

interface VideoPlayerProps {
  src: string;
  autoPlay?: boolean;
  showControls?: boolean;
  className?: string;
  userId?: string;
  courseId?: string;
  lessonId?: string;
  resumeTime?: number;
  onProgressUpdate?: () => void;
  userName?: string;
  onDurationChange?: (seconds: number) => void;
}

const WATERMARK_POSITIONS = [
  { top: '8%',     left: '4%',  bottom: 'auto', right: 'auto' },
  { top: '8%',     right: '4%', bottom: 'auto', left: 'auto'  },
  { bottom: '14%', left: '4%',  top: 'auto',    right: 'auto' },
  { bottom: '14%', right: '4%', top: 'auto',    left: 'auto'  },
  { top: '50%',    left: '4%',  bottom: 'auto', right: 'auto' },
  { top: '50%',    right: '4%', bottom: 'auto', left: 'auto'  },
];

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  autoPlay = false,
  showControls = true,
  className = "",
  userId,
  courseId,
  lessonId,
  resumeTime = 0,
  onProgressUpdate,
  userName,
  onDurationChange,
}) => {
  const videoRef     = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hlsRef           = useRef<Hls | null>(null);
  const stallTimerRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSeekingRef     = useRef(false);
  // One cache per VideoPlayer instance; cleared on lesson change
  const segmentCacheRef  = useRef(new LRUSegmentCache(300));

  // reinitKey increments to force a full HLS destroy+recreate after fatal errors
  const [reinitKey, setReinitKey] = useState(0);
  const [token, setToken]         = useState<string | null>(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted]     = useState(false);
  const [showControlsOverlay, setShowControlsOverlay] = useState(false);
  const [progress, setProgress]   = useState(0);
  const [duration, setDuration]   = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [buffered, setBuffered]   = useState(0);
  const [qualityLevels, setQualityLevels] = useState<any[]>([]);
  const [currentQuality, setCurrentQuality] = useState(-1);
  const [isStalled, setIsStalled] = useState(false);
  const [watermarkPos, setWatermarkPos] = useState(0);

  // Mutable refs — updated without causing HLS re-init
  const watchTimeRef        = useRef(0);
  const hasSentFinalUpdate  = useRef(false);
  const hasSetResumeTime    = useRef(false);
  const savedPositionRef    = useRef(0);   // position to restore after error-recovery reinit
  const networkRetryRef     = useRef(0);
  const resumeTimeRef       = useRef(resumeTime);
  const onDurationChangeRef = useRef(onDurationChange);
  const onProgressUpdateRef = useRef(onProgressUpdate);
  const userIdRef           = useRef(userId);
  const courseIdRef         = useRef(courseId);
  const lessonIdRef         = useRef(lessonId);
  const autoPlayRef         = useRef(autoPlay);

  // Keep prop refs in sync
  useEffect(() => { resumeTimeRef.current = resumeTime; },           [resumeTime]);
  useEffect(() => { onDurationChangeRef.current = onDurationChange; },[onDurationChange]);
  useEffect(() => { onProgressUpdateRef.current = onProgressUpdate; },[onProgressUpdate]);
  useEffect(() => { userIdRef.current   = userId;   }, [userId]);
  useEffect(() => { courseIdRef.current = courseId; }, [courseId]);
  useEffect(() => { lessonIdRef.current = lessonId; }, [lessonId]);
  useEffect(() => { autoPlayRef.current = autoPlay; }, [autoPlay]);

  // ── Token ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    setToken(localStorage.getItem('AUTH_TOKEN'));
  }, []);

  // ── Watermark rotation ────────────────────────────────────────────────────
  useEffect(() => {
    const id = setInterval(() => setWatermarkPos(p => (p + 1) % WATERMARK_POSITIONS.length), 15000);
    return () => clearInterval(id);
  }, []);

  // ── Watch-time tracking ───────────────────────────────────────────────────
  const sendWatchTimeUpdate = useCallback(
  async (
    lessonIdOverride?: string,
    watchTimeOverride?: number
  ) => {
    const uid = userIdRef.current;
    const cid = courseIdRef.current;
    const lid = lessonIdOverride ?? lessonIdRef.current;
    const watchTime = watchTimeOverride ?? watchTimeRef.current;

    if (
      uid &&
      cid &&
      lid &&
      watchTime > 0 &&
      !hasSentFinalUpdate.current
    ) {
      try {
        hasSentFinalUpdate.current = true;

        await updateLessonWatchTime(
          uid,
          cid,
          lid,
          watchTime
        );
      } catch (e) {
        console.error('Error updating watch time:', e);
      }
    }
  },
  []
);

  // Reset tracking + clear segment cache when lesson changes
 useEffect(() => {
  const previousLessonId = lessonIdRef.current;
  const previousWatchTime = watchTimeRef.current;

  if (
    previousLessonId &&
    previousWatchTime > 0 &&
    !hasSentFinalUpdate.current
  ) {
    sendWatchTimeUpdate(
      previousLessonId,
      previousWatchTime
    );
  }

  lessonIdRef.current = lessonId;

  watchTimeRef.current = 0;
  hasSentFinalUpdate.current = false;
  hasSetResumeTime.current = false;
  savedPositionRef.current = 0;
  networkRetryRef.current = 0;
  segmentCacheRef.current.clear();
}, [lessonId, sendWatchTimeUpdate]);

  // Track watch time while playing (updates ref every second)
  useEffect(() => {
    if (!isPlaying) return;
    const id = setInterval(() => {
      const video = videoRef.current;
      if (video && video.currentTime > watchTimeRef.current) {
        watchTimeRef.current = Math.floor(video.currentTime);
        onProgressUpdateRef.current?.();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [isPlaying]);

  // Periodically persist watch time to backend every 30 seconds while playing
  useEffect(() => {
    if (!isPlaying) return;
    const id = setInterval(() => {
      if (watchTimeRef.current > 0 && !hasSentFinalUpdate.current) {
        const uid = userIdRef.current;
        const cid = courseIdRef.current;
        const lid = lessonIdRef.current;
        if (uid && cid && lid) {
          updateLessonWatchTime(uid, cid, lid, watchTimeRef.current).catch(() => {});
        }
      }
    }, 30000);
    return () => clearInterval(id);
  }, [isPlaying]);

// Send on page unload
useEffect(() => {
  const handleBeforeUnload = () => {
    sendWatchTimeUpdate();
  };

  window.addEventListener('beforeunload', handleBeforeUnload);

  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };
}, [sendWatchTimeUpdate]);

  // ── Stall recovery (buffering spinner) ────────────────────────────────────
  const clearStallTimer = useCallback(() => {
    if (stallTimerRef.current) { clearTimeout(stallTimerRef.current); stallTimerRef.current = null; }
  }, []);

  const scheduleStallRecovery = useCallback(() => {
    clearStallTimer();
    stallTimerRef.current = setTimeout(() => {
      // Never disrupt a seek — the waiting event during seeking is normal
      if (isSeekingRef.current) return;
      const hls = hlsRef.current;
      if (!hls) return;
      console.warn('Stall recovery: calling recoverMediaError');
      try { hls.recoverMediaError(); } catch (e) { console.error('Stall recovery failed', e); }
    }, 5000);
  }, [clearStallTimer]);

  // ── Main HLS initialisation ───────────────────────────────────────────────
  // Re-runs when: src changes, token is ready, or reinitKey increments (error recovery)
  useEffect(() => {
    if (!token || !src) return;

    const video = videoRef.current;
    if (!video) return;

    // Destroy any existing HLS instance cleanly
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    setLoading(true);
    setError(null);
    setIsStalled(false);
    networkRetryRef.current = 0;

    // ── Video element events ─────────────────────────────────────────────
    const onPlay     = () => { setIsPlaying(true);  setIsStalled(false); clearStallTimer(); };
    const onPause    = () => setIsPlaying(false);
    const onEnded    = () => { setIsPlaying(false); sendWatchTimeUpdate(); };
    const onTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      setProgress((video.currentTime / (video.duration || 1)) * 100);
    };
    const onProgress = () => {
      if (!video.buffered.length) return;
      const end = video.buffered.end(video.buffered.length - 1);
      if (video.duration > 0) setBuffered((end / video.duration) * 100);
    };
    const onLoadedMetadata = () => {
      const d = video.duration || 0;
      setDuration(d);
      onDurationChangeRef.current?.(d);

      // Determine which position to resume from:
      // savedPositionRef > 0 means we're recovering from an error mid-playback
      const target = savedPositionRef.current > 0
        ? savedPositionRef.current
        : resumeTimeRef.current;

      if (target > 0 && target < d) {
        video.currentTime = target;
        setCurrentTime(target);
        setProgress((target / d) * 100);
      }
      savedPositionRef.current = 0; // clear after use
      hasSetResumeTime.current = true;
    };
    // During a seek the browser fires 'waiting' while buffering new segments —
    // that is normal and must NOT trigger stall recovery (would reset HLS.js).
    const onSeeking  = () => { isSeekingRef.current = true;  clearStallTimer(); setIsStalled(true); };
    const onSeeked   = () => { isSeekingRef.current = false; };
    const onWaiting  = () => {
      setIsStalled(true);
      if (!isSeekingRef.current) scheduleStallRecovery(); // only for genuine stalls
    };
    const onCanPlay  = () => { setIsStalled(false); clearStallTimer(); };
    const onPlaying  = () => { setIsStalled(false); clearStallTimer(); setLoading(false); isSeekingRef.current = false; };
    const onStalled  = () => {
      if (!isSeekingRef.current) scheduleStallRecovery(); // only for genuine stalls
      setIsStalled(true);
    };

    video.addEventListener('play',           onPlay);
    video.addEventListener('pause',          onPause);
    video.addEventListener('ended',          onEnded);
    video.addEventListener('timeupdate',     onTimeUpdate);
    video.addEventListener('progress',       onProgress);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('seeking',        onSeeking);
    video.addEventListener('seeked',         onSeeked);
    video.addEventListener('waiting',        onWaiting);
    video.addEventListener('canplay',        onCanPlay);
    video.addEventListener('playing',        onPlaying);
    video.addEventListener('stalled',        onStalled);

    // ── HLS setup ────────────────────────────────────────────────────────
    if (Hls.isSupported()) {
      const hls = new Hls({
        xhrSetup: (xhr) => {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        },
        // Cache-backed fragment loader — serves already-downloaded segments instantly
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        fLoader: makeCachedFragLoader(segmentCacheRef.current) as any,
        enableWorker:             true,
        lowLatencyMode:           false,   // VOD — never use low-latency mode

        // ── Prefetch / buffer ─────────────────────────────────────────────
        // Typical HLS segment = 6s → 30s covers ~5 segments ahead (enough for smooth play)
        startFragPrefetch:        true,    // start fetching segments before play()
        maxBufferLength:          30,      // 30s ahead — fast startup without over-buffering
        maxMaxBufferLength:       60,      // absolute ceiling kept low for large videos
        maxBufferSize:            100 * 1000 * 1000, // 100 MB cache
        backBufferLength:         60,      // keep 60s behind for seeking back

        // ── Quality ───────────────────────────────────────────────────────
        startLevel:               0,       // always start at lowest quality for fast startup; ABR switches up
        capLevelToPlayerSize:     true,    // don't download higher res than screen

        // ── Retry / resilience ────────────────────────────────────────────
        fragLoadingMaxRetry:      6,
        fragLoadingRetryDelay:    1000,
        fragLoadingMaxRetryTimeout: 64000,
        manifestLoadingMaxRetry:  4,
        manifestLoadingRetryDelay: 1000,
        levelLoadingMaxRetry:     4,
        levelLoadingRetryDelay:   1000,
      });

      hlsRef.current = hls;
      hls.loadSource(src);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
        setLoading(false);
        setQualityLevels(data.levels);
        if (autoPlayRef.current) {
          video.play().catch(e => console.log('Autoplay prevented:', e));
        }
      });

      hls.on(Hls.Events.LEVEL_SWITCHED, (_, data) => setCurrentQuality(data.level));

      hls.on(Hls.Events.BUFFER_APPENDED, () => {
        if (video.buffered.length > 0) {
          const end = video.buffered.end(video.buffered.length - 1);
          if (video.duration > 0) setBuffered((end / video.duration) * 100);
        }
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (!data.fatal) return;

        console.warn('Fatal HLS error:', data.type, data.details);

        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          if (networkRetryRef.current < 4) {
            networkRetryRef.current += 1;
            // Save current playback position so we can resume after reinit
            savedPositionRef.current = video.currentTime || 0;
            const delay = Math.min(1500 * networkRetryRef.current, 10000);
            console.log(`Network error — full HLS reinit in ${delay}ms (attempt ${networkRetryRef.current})`);
            setTimeout(() => {
              // Increment reinitKey to trigger a complete HLS destroy + recreate
              setReinitKey(k => k + 1);
            }, delay);
          } else {
            setError('Video stream unavailable. Please refresh the page.');
          }
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          console.log('Media error — attempting recovery');
          hls.recoverMediaError();
        } else {
          setError('Failed to load video. Please refresh the page.');
        }
      });

    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS
      video.src = src;
      setLoading(false);
      if (autoPlayRef.current) video.play().catch(e => console.log('Autoplay prevented:', e));
    } else {
      setError("Your browser doesn't support HLS video playback.");
    }

    // ── Cleanup ──────────────────────────────────────────────────────────
    return () => {
      clearStallTimer();
      video.removeEventListener('play',           onPlay);
      video.removeEventListener('pause',          onPause);
      video.removeEventListener('ended',          onEnded);
      video.removeEventListener('timeupdate',     onTimeUpdate);
      video.removeEventListener('progress',       onProgress);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('seeking',        onSeeking);
      video.removeEventListener('seeked',         onSeeked);
      video.removeEventListener('waiting',        onWaiting);
      video.removeEventListener('canplay',        onCanPlay);
      video.removeEventListener('playing',        onPlaying);
      video.removeEventListener('stalled',        onStalled);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      sendWatchTimeUpdate();
    };
  // reinitKey is intentionally included so errors trigger a full clean reinit
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, token, reinitKey]);

  // ── Controls visibility ───────────────────────────────────────────────────
  useEffect(() => {
    let t: ReturnType<typeof setTimeout>;
    if (isPlaying) { t = setTimeout(() => setShowControlsOverlay(false), 3000); }
    else           { setShowControlsOverlay(true); }
    return () => clearTimeout(t);
  }, [isPlaying]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const togglePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.paused ? video.play().catch(() => {}) : video.pause();
  }, []);

  const skip = useCallback((seconds: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.min(Math.max(0, video.currentTime + seconds), video.duration || 0);
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  }, []);

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    if (!video) return;
    const rect = e.currentTarget.getBoundingClientRect();
    video.currentTime = ((e.clientX - rect.left) / rect.width) * (video.duration || 0);
  }, []);

  const handleFullscreen = useCallback(() => {
    const isMobile = window.matchMedia('(max-width: 768px)').matches || 'ontouchstart' in window;
    if (!isMobile) {
      containerRef.current?.requestFullscreen?.();
      return;
    }

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      // iOS Safari doesn't support requestFullscreen() on a plain <div>, and
      // doesn't support the Screen Orientation API at all — the native video
      // fullscreen player is the only thing that reliably goes fullscreen and
      // rotates with the device here.
      const video = videoRef.current as any;
      video?.webkitEnterFullscreen?.();
      return;
    }

    containerRef.current?.requestFullscreen?.()
      ?.then(() => (window.screen.orientation as any)?.lock?.('landscape'))
      .catch(() => {
        // Some mobile browsers reject orientation lock (or fullscreen itself)
        // depending on user settings — fail silently and keep portrait fullscreen
        // rather than breaking playback.
      });
  }, []);

  // Release the orientation lock once the user exits fullscreen, so the phone
  // goes back to following its normal rotation behavior.
  useEffect(() => {
    const onFullscreenChange = () => {
      if (!document.fullscreenElement) {
        (window.screen.orientation as any)?.unlock?.();
      }
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const setQualityLevel = useCallback((level: number) => {
    if (hlsRef.current) { hlsRef.current.nextLevel = level; setCurrentQuality(level); }
  }, []);

  const formatTime = useCallback((time: number) => {
    const h  = Math.floor(time / 3600);
    const m  = Math.floor((time % 3600) / 60);
    const s  = Math.floor(time % 60);
    const mm = m < 10 ? `0${m}` : `${m}`;
    const ss = s < 10 ? `0${s}` : `${s}`;
    return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────
  if (!token) {
    return <div className="flex items-center justify-center h-full bg-black rounded-lg text-white text-sm">Loading…</div>;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-black rounded-lg gap-3 p-4">
        <p className="text-red-400 text-sm text-center">{error}</p>
        <button
          onClick={() => { setError(null); setLoading(true); networkRetryRef.current = 0; setReinitKey(k => k + 1); }}
          className="px-4 py-2 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full rounded-lg overflow-hidden bg-black ${className}`}
      onMouseMove={() => setShowControlsOverlay(true)}
    >
      {/* Loading / buffering spinner */}
      {(loading || isStalled) && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 z-10 rounded-lg gap-2">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500" />
          {isStalled && <span className="text-white/60 text-xs">Buffering…</span>}
        </div>
      )}

      <video
        ref={videoRef}
        controls={false}
        className="w-full h-full object-contain"
        playsInline
        preload="auto"
      />

      {/* Watermark */}
      {(userName || userId) && (
        <div
          className="absolute pointer-events-none select-none z-20 transition-all duration-1000"
          style={WATERMARK_POSITIONS[watermarkPos]}
        >
          <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px', fontWeight: 600, lineHeight: 1.4, textShadow: '0 1px 3px rgba(0,0,0,0.7)', letterSpacing: '0.3px', userSelect: 'none' }}>
            {userName && <div>{userName}</div>}
            {userId   && <div>ID: {userId}</div>}
          </div>
        </div>
      )}

      {/* Controls overlay */}
      {showControls && (
        <div
          className={`absolute inset-0 bg-gradient-to-t from-black/70 to-transparent transition-opacity duration-300 ${showControlsOverlay ? 'opacity-100' : 'opacity-0'}`}
          onMouseEnter={() => setShowControlsOverlay(true)}
          onMouseLeave={() => isPlaying && setShowControlsOverlay(false)}
        >
          {/* Centre controls — backward / play-pause / forward */}
          <div className="absolute inset-0 flex items-center justify-center gap-6">
            <button
              onClick={() => skip(-10)}
              className="group flex flex-col items-center text-white/80 hover:text-white transition-colors"
              title="Back 10s"
            >
              <RotateCcw size={28} />
              <span className="text-[10px] mt-0.5 font-semibold">10</span>
            </button>

            <button onClick={togglePlayPause} className="bg-black/50 rounded-full p-3 backdrop-blur-sm hover:bg-black/70 transition-all">
              {isPlaying ? <Pause className="text-white" size={26} /> : <Play className="text-white" size={26} />}
            </button>

            <button
              onClick={() => skip(10)}
              className="group flex flex-col items-center text-white/80 hover:text-white transition-colors"
              title="Forward 10s"
            >
              <RotateCw size={28} />
              <span className="text-[10px] mt-0.5 font-semibold">10</span>
            </button>
          </div>

          {/* Bottom controls */}
          <div className="absolute bottom-0 left-0 right-0 p-3">
            {/* Progress bar */}
            <div className="w-full h-1.5 bg-gray-600 rounded-full cursor-pointer mb-2.5 relative" onClick={handleProgressClick}>
              <div className="absolute top-0 left-0 h-full bg-gray-500 rounded-full" style={{ width: `${buffered}%` }} />
              <div className="relative h-full bg-purple-500 rounded-full" style={{ width: `${progress}%` }}>
                <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-3 h-3 bg-white rounded-full shadow" />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button onClick={togglePlayPause} className="text-white hover:text-purple-400 transition-colors">
                  {isPlaying ? <Pause size={18} /> : <Play size={18} />}
                </button>

                {/* ← 10s / 10s → */}
                <button onClick={() => skip(-10)} title="Back 10s" className="text-white hover:text-purple-400 transition-colors flex items-center gap-0.5">
                  <RotateCcw size={15} />
                  <span className="text-[10px] font-bold leading-none">10</span>
                </button>
                <button onClick={() => skip(10)} title="Forward 10s" className="text-white hover:text-purple-400 transition-colors flex items-center gap-0.5">
                  <span className="text-[10px] font-bold leading-none">10</span>
                  <RotateCw size={15} />
                </button>

                <button onClick={toggleMute} className="text-white hover:text-purple-400 transition-colors">
                  {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
                <span className="text-white text-xs tabular-nums">{formatTime(currentTime)} / {formatTime(duration)}</span>
              </div>

              <div className="flex items-center space-x-3">
                {qualityLevels.length > 1 && (
                  <div className="relative group">
                    <button className="text-white hover:text-purple-400 text-xs flex items-center">
                      <Settings size={14} className="mr-1" />
                      {currentQuality >= 0 ? `${qualityLevels[currentQuality]?.height}p` : 'Auto'}
                    </button>
                    <div className="absolute bottom-full right-0 mb-2 min-w-max bg-black/90 rounded-lg py-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto z-10">
                      <button onClick={() => setQualityLevel(-1)} className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-700 ${currentQuality === -1 ? 'text-purple-400' : 'text-white'}`}>Auto</button>
                      {qualityLevels.map((level, i) => (
                        <button key={i} onClick={() => setQualityLevel(i)} className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-700 ${currentQuality === i ? 'text-purple-400' : 'text-white'}`}>
                          {level.height}p
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <button onClick={handleFullscreen} className="text-white hover:text-purple-400 transition-colors">
                  <Maximize size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
